/**
 * Account Creation API Service
 * 
 * HTTP client for account creation backend API.
 * Handles session creation, email validation, and account creation.
 * 
 * Backend API: https://svcs-lore-00-nonprod.endpoints.protected-00-349215.cloud.goog/services/account-creation-api
 * 
 * @module services/api/AccountCreationApiService
 */

import axios, { AxiosInstance } from 'axios';
import {
  SessionRequest,
  SessionResponse,
  EmailExistsResponse,
  AccountCreationRequest,
  AccountCreationResponse,
} from '@/types/accountCreation';

/**
 * Get base URL for account creation API based on environment
 */
const getBaseUrl = (): string => {
  const env = process.env.REACT_APP_ENV || 'development';
  
  if (env === 'production' || env === 'prod') {
    return 'https://svcs-lore-00.endpoints.protected-00-349215.cloud.goog/services/account-creation-api';
  }
  
  // Default to nonprod for development/staging
  return (
    process.env.REACT_APP_ACCOUNT_CREATION_API_URL ||
    'https://svcs-lore-00-nonprod.endpoints.protected-00-349215.cloud.goog/services/account-creation-api'
  );
};

/**
 * Account Creation API Service
 * 
 * Singleton service for account creation operations.
 */
class AccountCreationApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: getBaseUrl(),
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000, // 30 second timeout
    });

    // Request interceptor for logging
    this.api.interceptors.request.use(
      (config) => {
        console.log('[AccountCreationAPI] Request:', config.method?.toUpperCase(), config.url);
        return config;
      },
      (error) => {
        console.error('[AccountCreationAPI] Request error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor for logging and error handling
    this.api.interceptors.response.use(
      (response) => {
        console.log('[AccountCreationAPI] Response:', response.status, response.config.url);
        return response;
      },
      (error) => {
        if (error.response) {
          console.error('[AccountCreationAPI] Response error:', {
            status: error.response.status,
            data: error.response.data,
            url: error.config?.url,
          });
        } else if (error.request) {
          console.error('[AccountCreationAPI] Network error:', error.message);
        } else {
          console.error('[AccountCreationAPI] Error:', error.message);
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Create a new session for account creation flow
   * 
   * @param request - Session request with device info
   * @returns Session response with session ID
   * 
   * @example
   * const deviceInfo = readDeviceInformation();
   * const session = await AccountCreationApiService.createSession({
   *   device_info: JSON.stringify(deviceInfo),
   *   visit_source: 1,
   *   coupon_code: null
   * });
   */
  async createSession(request: SessionRequest): Promise<SessionResponse> {
    const response = await this.api.post<SessionResponse>('/api/v1/obflow/session', request);
    return response.data;
  }

  /**
   * Check if an email address already exists
   * 
   * @param email - Email address to check
   * @returns Whether the email exists
   * 
   * @example
   * const result = await AccountCreationApiService.checkEmailExists('user@example.com');
   * if (result.exists) {
   *   console.log('Email already registered');
   * }
   */
  async checkEmailExists(email: string): Promise<EmailExistsResponse> {
    try {
      const response = await this.api.get<EmailExistsResponse>(
        `/api/v1/user/check-email/${encodeURIComponent(email)}`
      );
      return response.data;
    } catch (error: any) {
      // Backend returns 404 if email doesn't exist
      if (error.response?.status === 404) {
        return { exists: false };
      }
      throw error;
    }
  }

  /**
   * Create a new account
   * 
   * @param request - Account creation request
   * @returns Account creation response with tokens
   * 
   * @example
   * const response = await AccountCreationApiService.createAccount({
   *   email: 'user@example.com',
   *   password: 'securePassword123',
   *   oe_user_session_id: sessionId,
   *   auth_provider: 'gcip'
   * });
   * // Use response.access_token to sign in
   */
  async createAccount(request: AccountCreationRequest): Promise<AccountCreationResponse> {
    const response = await this.api.post<AccountCreationResponse>(
      '/api/v1/account-creation/create',
      request
    );
    return response.data;
  }
}

// Export singleton instance
const accountCreationApiService = new AccountCreationApiService();
export default accountCreationApiService;

